import { createReducer, on } from '@ngrx/store';
import {
  CheckoutEventSuccess,
  CheckoutInitialisation,
  CustomerDetails,
  PaymentFrequency,
  QuoteResponse,
  RegisteredItem,
  RegistrationRequest,
  RegistrationResponse,
  RegistrationSystemCode,
  RegistriaUsersResponse,
  SerializationCodeResponse,
} from '@us-registration/util-models';
import * as RegistrationActions from './registration.actions';

export const REGISTRATION_FEATURE_KEY = 'registration';

export interface RegistrationState {
  checkoutInitialisation?: CheckoutInitialisation;
  checkoutSuccess?: CheckoutEventSuccess;
  registeredItem?: RegisteredItem;
  paymentFrequency?: PaymentFrequency;
  quoteError?: number;
  quoteResponse?: QuoteResponse;
  registrationCustomerDetails?: CustomerDetails;
  registrationError?: Error;
  registrationLoading: boolean;
  registrationRequest?: RegistrationRequest;
  registrationSystemCode: RegistrationSystemCode;
  registriaUserResponse?: RegistriaUsersResponse;
  serializationCodeResponse?: SerializationCodeResponse;
  userToken?: string;
}

export interface RegistrationPartialState {
  readonly [REGISTRATION_FEATURE_KEY]: RegistrationState;
}

export const initialState: RegistrationState = {
  checkoutInitialisation: undefined,
  checkoutSuccess: undefined,
  registeredItem: undefined,
  paymentFrequency: PaymentFrequency.Monthly,
  quoteError: undefined,
  quoteResponse: undefined,
  registrationCustomerDetails: undefined,
  registrationError: undefined,
  registrationLoading: false,
  registrationRequest: undefined,
  registrationSystemCode: RegistrationSystemCode.InternetProductRegistration,
  registriaUserResponse: undefined,
  serializationCodeResponse: undefined,
  userToken: undefined,
};

export const registrationReducer = createReducer(
  initialState,
  on(
    RegistrationActions.getQuote,
    (
      state: RegistrationState,
      action: { registrationRequest: RegistrationRequest }
    ): RegistrationState => ({
      ...state,
      registeredItem: undefined,
      registrationLoading: true,
      registrationCustomerDetails: action.registrationRequest.customer,
      registrationRequest: action.registrationRequest,
    })
  ),
  on(
    RegistrationActions.getQuoteSuccess,
    (
      state: RegistrationState,
      action: { quoteResponse: QuoteResponse }
    ): RegistrationState => ({
      ...state,
      quoteResponse: action.quoteResponse,
      registrationLoading: false,
    })
  ),
  on(
    RegistrationActions.getQuoteNoContent,
    (
      state: RegistrationState,
      action: { statusCode: number }
    ): RegistrationState => ({
      ...state,
      quoteError: action.statusCode,
      registrationLoading: false,
    })
  ),
  on(
    RegistrationActions.getQuoteFailure,
    (
      state: RegistrationState,
      action: { statusCode: number }
    ): RegistrationState => ({
      ...state,
      quoteError: action.statusCode,
      registrationLoading: false,
    })
  ),
  on(
    RegistrationActions.registerAppliance,
    (
      state: RegistrationState,
      action: { registrationRequest: RegistrationRequest }
    ): RegistrationState => ({
      ...state,
      registeredItem: undefined,
      registrationLoading: true,
      registrationCustomerDetails: action.registrationRequest.customer,
      registrationRequest: action.registrationRequest,
    })
  ),
  on(
    RegistrationActions.registerApplianceSuccess,
    (
      state: RegistrationState,
      action: { registrationResponse: RegistrationResponse }
    ): RegistrationState => ({
      ...state,
      registeredItem: action.registrationResponse[0],
      registrationLoading: false,
    })
  ),
  on(
    RegistrationActions.registerApplianceFailure,
    (
      state: RegistrationState,
      action: { error: Error }
    ): RegistrationState => ({
      ...state,
      registrationLoading: false,
      registrationError: action.error,
    })
  ),
  on(
    RegistrationActions.addSerializationCodeResponse,
    (
      state: RegistrationState,
      action: { serializationCodeResponse: SerializationCodeResponse }
    ): RegistrationState => ({
      ...state,
      serializationCodeResponse: action.serializationCodeResponse,
    })
  ),
  on(
    RegistrationActions.addRegistrationSystemCode,
    (
      state: RegistrationState,
      action: { registrationSystemCode: RegistrationSystemCode }
    ): RegistrationState => ({
      ...state,
      registrationSystemCode: action.registrationSystemCode,
    })
  ),
  on(
    RegistrationActions.addRegistriaUserResponse,
    (
      state: RegistrationState,
      action: { registriaUserResponse: RegistriaUsersResponse }
    ): RegistrationState => ({
      ...state,
      registriaUserResponse: action.registriaUserResponse,
    })
  ),
  on(
    RegistrationActions.addUserToken,
    (
      state: RegistrationState,
      action: { userToken: string }
    ): RegistrationState => ({
      ...state,
      userToken: action.userToken,
    })
  ),
  on(
    RegistrationActions.addCheckoutInitialisation,
    (
      state: RegistrationState,
      action: { checkoutInitialisation: CheckoutInitialisation }
    ): RegistrationState => ({
      ...state,
      checkoutInitialisation: action.checkoutInitialisation,
    })
  ),
  on(
    RegistrationActions.checkoutSuccess,
    (
      state: RegistrationState,
      action: { checkoutSuccess: CheckoutEventSuccess }
    ): RegistrationState => ({
      ...state,
      checkoutSuccess: action.checkoutSuccess,
    })
  ),
  on(
    RegistrationActions.addPaymentFrequency,
    (
      state: RegistrationState,
      action: { paymentFrequency: PaymentFrequency }
    ): RegistrationState => ({
      ...state,
      paymentFrequency: action.paymentFrequency,
    })
  )
);
